import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Users, UserPlus, DollarSign, Activity, MousePointerClick, Zap, Server, Loader2, Gift, Check } from "lucide-react";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function KpiTile({ icon, label, value, sub, testid, dark }) {
  return (
    <div
      data-testid={testid}
      className={`border-2 border-[#111] p-5 flex flex-col ${dark ? "bg-[#111] text-[#f6f6f4]" : "bg-[#fafaf9]"}`}
    >
      <div className="flex items-center gap-2">
        <div className={`inline-flex items-center justify-center w-7 h-7 border-2 ${dark ? "border-[#f6f6f4]" : "border-[#111]"}`}>
          {icon}
        </div>
        <div className={`font-mono text-[10px] tracking-[0.3em] uppercase font-bold ${dark ? "text-[#aaa]" : "text-[#555]"}`}>
          {label}
        </div>
      </div>
      <div className="mt-4 font-display text-4xl font-extrabold tracking-tight">{value}</div>
      {sub && (
        <div className={`mt-1 font-mono text-[10px] tracking-[0.2em] uppercase font-bold ${dark ? "text-[#aaa]" : "text-[#777]"}`}>
          {sub}
        </div>
      )}
    </div>
  );
}

function SignupSparkline({ data }) {
  // Simple inline SVG bar chart for last 30 days
  if (!data || data.length === 0) {
    return (
      <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#777] font-bold">
        No signups in the last 30 days yet.
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  const barW = 100 / data.length;
  return (
    <div data-testid="admin-signups-chart">
      <svg viewBox="0 0 100 40" className="w-full h-24" preserveAspectRatio="none">
        {data.map((d, i) => {
          const h = Math.max(1.5, (d.count / max) * 36);
          return (
            <rect
              key={d.day}
              x={i * barW + 0.5}
              y={40 - h}
              width={barW - 1}
              height={h}
              fill="#111"
            />
          );
        })}
      </svg>
      <div className="mt-2 flex justify-between font-mono text-[9px] tracking-[0.15em] uppercase text-[#777] font-bold">
        <span>{data[0]?.day}</span>
        <span>{data[data.length - 1]?.day}</span>
      </div>
    </div>
  );
}

export default function Admin() {
  const { user, status } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paidUsers, setPaidUsers] = useState([]);
  const [grantForm, setGrantForm] = useState({ email: "", password: "", tier: "fantasy", duration: "month", note: "" });
  const [granting, setGranting] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [resetPw, setResetPw] = useState("");
  const [resetting, setResetting] = useState(false);

  async function submitReset(e) {
    e.preventDefault();
    setResetting(true);
    try {
      await axios.post(`${API}/admin/users/reset-password`, { email: resetFor, password: resetPw }, { withCredentials: true });
      toast.success(`Password updated for ${resetFor}`);
      setResetFor(null);
      setResetPw("");
    } catch (err) {
      toast.error(formatApiError(err) || "Reset failed.");
    } finally {
      setResetting(false);
    }
  }

  async function refreshPaidUsers() {
    try {
      const r = await axios.get(`${API}/admin/users?paid_only=true&limit=200`, { withCredentials: true });
      setPaidUsers(r.data?.users || []);
    } catch (e) {
      // silent — the main stats call surfaces errors
    }
  }

  async function refreshStats() {
    try {
      const r = await axios.get(`${API}/admin/stats`, { withCredentials: true });
      setStats(r.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load stats.");
    }
  }

  async function submitGrant(e) {
    e.preventDefault();
    const email = grantForm.email.trim();
    if (!email) return;
    setGranting(true);
    try {
      const r = await axios.post(`${API}/admin/users/grant`, {
        email,
        tier: grantForm.tier,
        duration: grantForm.duration,
        note: grantForm.note || null,
        password: grantForm.password || null,
      }, { withCredentials: true });
      const verb = { created_user: "Added", created_shell: "Pre-provisioned", updated: "Updated" }[r.data.action] || "Updated";
      toast.success(`${verb} ${email} → ${r.data.tier.toUpperCase()}`, {
        description: r.data.password_set ? "They can sign in on the website and the app with the password you set." : undefined,
      });
      setGrantForm({ email: "", password: "", tier: grantForm.tier, duration: grantForm.duration, note: "" });
      await Promise.all([refreshStats(), refreshPaidUsers()]);
    } catch (err) {
      toast.error(formatApiError(err) || "Grant failed.");
    } finally {
      setGranting(false);
    }
  }

  // Guard: non-admins / anon get bounced
  useEffect(() => {
    if (status === "checking") return;
    if (status !== "in") {
      toast.error("Sign in required.");
      navigate("/", { replace: true });
      return;
    }
    if (user?.role !== "admin") {
      toast.error("Admin only.");
      navigate("/tracker", { replace: true });
    }
  }, [status, user, navigate]);

  // Fetch stats whenever we're confirmed admin
  useEffect(() => {
    if (status !== "in" || user?.role !== "admin") return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        await refreshStats();
        await refreshPaidUsers();
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, user]);

  const topPartners = useMemo(() => stats?.affiliate?.by_partner?.slice(0, 6) || [], [stats]);

  if (status === "checking" || !user || user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center font-mono text-xs tracking-[0.3em] uppercase text-[#555] font-bold">
        <Loader2 className="spin-slow mr-2" size={14} />
        Loading admin…
      </div>
    );
  }

  return (
    <main className="min-h-screen w-full bg-[#fafaf9] text-[#111]" data-testid="admin-page">
      <div className="border-b-2 border-[#111] bg-[#111] text-[#f6f6f4]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-2 flex items-center justify-between gap-6">
          <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold">
            Admin · Operator Dashboard · {user.email}
          </div>
          <Link
            to="/tracker"
            className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-[0.3em] uppercase font-bold hover:opacity-70"
            data-testid="admin-back-link"
          >
            <ArrowLeft size={11} strokeWidth={2.6} />
            Back to schedule
          </Link>
        </div>
      </div>

      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-10">
        <div className="flex items-center gap-3">
          <span className="bullet" />
          <span className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
            01 — Live KPIs
          </span>
        </div>
        <h1 className="mt-4 font-display text-4xl md:text-5xl font-extrabold tracking-tight">
          How the business is doing.
        </h1>
        <p className="mt-2 text-[#555] max-w-2xl">
          Users, affiliate clicks, and estimated API spend — pulled live from Mongo on every refresh.
        </p>

        {error && (
          <div className="mt-6 border-2 border-[#111] p-4 bg-[#fee] text-[#111] text-sm" data-testid="admin-error">
            {error}
          </div>
        )}

        {loading && !stats && (
          <div className="mt-10 font-mono text-xs tracking-[0.3em] uppercase text-[#555] font-bold">
            Loading…
          </div>
        )}

        {stats && (
          <>
            {/* Row 1 — Users */}
            <div className="mt-8 grid md:grid-cols-4 gap-4" data-testid="admin-user-kpis">
              <KpiTile
                icon={<Users size={14} strokeWidth={2.6} />}
                label="Total users"
                value={stats.users.total.toLocaleString()}
                sub={`${Object.entries(stats.users.by_provider).map(([k, v]) => `${k}: ${v}`).join(" · ")}`}
                testid="admin-kpi-users-total"
                dark
              />
              <KpiTile
                icon={<UserPlus size={14} strokeWidth={2.6} />}
                label="Signups · 7d"
                value={stats.users.last_7d.toLocaleString()}
                sub={`${stats.users.last_30d} in last 30d`}
                testid="admin-kpi-users-7d"
              />
              <KpiTile
                icon={<Activity size={14} strokeWidth={2.6} />}
                label="Active sessions"
                value={stats.sessions.active.toLocaleString()}
                sub="7-day cookie sessions"
                testid="admin-kpi-sessions-active"
              />
              <KpiTile
                icon={<MousePointerClick size={14} strokeWidth={2.6} />}
                label="Affiliate clicks · 7d"
                value={stats.affiliate.clicks_last_7d.toLocaleString()}
                sub={`${stats.affiliate.clicks_total.toLocaleString()} total`}
                testid="admin-kpi-affiliate-7d"
              />
            </div>

            {/* Row 2 — Revenue placeholders */}
            <div className="mt-6 grid md:grid-cols-3 gap-4" data-testid="admin-revenue-kpis">
              <KpiTile
                icon={<DollarSign size={14} strokeWidth={2.6} />}
                label="MRR"
                value={`$${stats.subscriptions.mrr.toFixed(2)}`}
                sub={`${stats.subscriptions.paid_users} paid users`}
                testid="admin-kpi-mrr"
                dark
              />
              <KpiTile
                icon={<DollarSign size={14} strokeWidth={2.6} />}
                label="ARR"
                value={`$${stats.subscriptions.arr.toFixed(2)}`}
                sub={`G:${stats.subscriptions.guide_users} · F:${stats.subscriptions.fantasy_users ?? 0} · P:${stats.subscriptions.pro_users ?? 0}`}
                testid="admin-kpi-arr"
              />
              <KpiTile
                icon={<Zap size={14} strokeWidth={2.6} />}
                label="Est. API spend"
                value={`$${(stats.costs.odds_api_dollar_estimate + stats.costs.llm_dollar_estimate).toFixed(2)}`}
                sub={`${stats.costs.odds_api_credits_used_estimate} odds credits · ${stats.costs.llm_calls_estimate} LLM calls`}
                testid="admin-kpi-api-cost"
              />
            </div>

            {/* Row 3 — Signups chart */}
            <section className="mt-10 border-2 border-[#111] p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="bullet" />
                <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                  02 — Signups · last 30 days
                </h2>
              </div>
              <SignupSparkline data={stats.users.signups_by_day_last_30d} />
            </section>

            {/* Row 3.5 — Manual tier grants */}
            <section className="mt-6 border-2 border-[#111] p-6" data-testid="admin-grant-section">
              <div className="flex items-center gap-3 mb-2">
                <span className="bullet" />
                <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                  03 — Add a person / grant a plan
                </h2>
              </div>
              <p className="text-sm text-[#555] mb-4 max-w-3xl">
                Add someone by email, set their password and plan, and they can sign in on the website and the iPhone app right away. If the email already has an account, the plan updates (and the password is replaced if you enter one). Leave the password blank to only grant a plan.
              </p>
              <form onSubmit={submitGrant} className="grid md:grid-cols-6 gap-3 items-end">
                <div className="md:col-span-2">
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Email</label>
                  <input
                    type="email"
                    required
                    value={grantForm.email}
                    onChange={(e) => setGrantForm({ ...grantForm, email: e.target.value })}
                    data-testid="admin-grant-email"
                    placeholder="friend@example.com"
                    className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Password</label>
                  <input
                    type="text"
                    minLength={8}
                    autoComplete="off"
                    value={grantForm.password}
                    onChange={(e) => setGrantForm({ ...grantForm, password: e.target.value })}
                    data-testid="admin-grant-password"
                    placeholder="8+ characters"
                    className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Plan</label>
                  <select
                    value={grantForm.tier}
                    onChange={(e) => setGrantForm({ ...grantForm, tier: e.target.value })}
                    data-testid="admin-grant-tier"
                    className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm uppercase font-bold focus-ring"
                  >
                    <option value="guide">Guide</option>
                    <option value="fantasy">Fantasy</option>
                    <option value="pro">Pro</option>
                    <option value="free">Free (revoke)</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Access for</label>
                  <select
                    value={grantForm.duration}
                    onChange={(e) => setGrantForm({ ...grantForm, duration: e.target.value })}
                    data-testid="admin-grant-duration"
                    className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm font-bold focus-ring"
                  >
                    <option value="week">1 week</option>
                    <option value="month">1 month</option>
                    <option value="year">1 year</option>
                  </select>
                </div>
                <button
                  type="submit"
                  disabled={granting || !grantForm.email.trim()}
                  data-testid="admin-grant-submit"
                  className="inline-flex items-center justify-center gap-2 border-2 border-[#111] bg-[#111] text-[#f6f6f4] px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-sm hover:bg-[#333] transition-colors focus-ring active:translate-y-px disabled:opacity-50"
                >
                  {granting ? <Loader2 size={14} className="spin-slow" /> : <Gift size={14} strokeWidth={2.6} />}
                  Add / grant
                </button>
                <div className="md:col-span-6">
                  <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Note (optional)</label>
                  <input
                    type="text"
                    value={grantForm.note}
                    onChange={(e) => setGrantForm({ ...grantForm, note: e.target.value })}
                    data-testid="admin-grant-note"
                    placeholder="e.g. Paid $60 via Venmo, 2026-10-05"
                    className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
                  />
                </div>
              </form>

              {resetFor && (
                <form onSubmit={submitReset} className="mt-6 border-2 border-[#111] p-4 flex flex-wrap items-end gap-3" data-testid="admin-reset-form">
                  <div className="flex-1 min-w-[220px]">
                    <label className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">New password for {resetFor}</label>
                    <input
                      type="text"
                      required
                      minLength={8}
                      autoComplete="off"
                      value={resetPw}
                      onChange={(e) => setResetPw(e.target.value)}
                      data-testid="admin-reset-password-input"
                      placeholder="8+ characters"
                      className="mt-1 w-full border-2 border-[#111] bg-transparent px-3 py-2 font-mono text-sm focus-ring"
                    />
                  </div>
                  <button type="submit" disabled={resetting || resetPw.length < 8} data-testid="admin-reset-password-submit"
                    className="border-2 border-[#111] bg-[#111] text-white px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-sm disabled:opacity-50">
                    {resetting ? "Saving…" : "Save password"}
                  </button>
                  <button type="button" onClick={() => setResetFor(null)} data-testid="admin-reset-password-cancel"
                    className="border-2 border-[#111] px-4 py-2 font-display font-extrabold uppercase tracking-[0.12em] text-sm">
                    Cancel
                  </button>
                </form>
              )}

              {/* Paid users table */}
              <div className="mt-6 overflow-x-auto">
                <div className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold mb-2">
                  Active paid users · {paidUsers.length}
                </div>
                {paidUsers.length === 0 ? (
                  <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#777] font-bold py-4">
                    No paid users yet.
                  </div>
                ) : (
                  <table className="w-full text-sm" data-testid="admin-paid-users-table">
                    <thead>
                      <tr className="border-b-2 border-[#111]">
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Email</th>
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Tier</th>
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Expires</th>
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Source</th>
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Note</th>
                        <th className="text-right py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Password</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paidUsers.map((u) => (
                        <tr key={u.id || u.email} className="border-b border-[#eee]">
                          <td className="py-2 font-display font-bold">{u.email}</td>
                          <td className="py-2 font-mono text-[11px] tracking-[0.2em] uppercase font-extrabold">
                            {u.tier}
                          </td>
                          <td className="py-2 font-mono text-[11px] text-[#555]">
                            {u.tier_expires_at ? new Date(u.tier_expires_at).toLocaleDateString() : "—"}
                          </td>
                          <td className="py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#777]">
                            {u.tier_source || "—"}
                          </td>
                          <td className="py-2 text-[12px] text-[#555] truncate max-w-[260px]">
                            {u.tier_note || ""}
                          </td>
                          <td className="py-2 text-right">
                            <button
                              type="button"
                              onClick={() => { setResetFor(u.email); setResetPw(""); }}
                              data-testid={`admin-reset-password-${u.email}`}
                              className="font-mono text-[10px] tracking-[0.2em] uppercase font-bold border-2 border-[#111] px-2 py-1 hover:bg-[#111] hover:text-white transition-colors"
                            >
                              Reset
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </section>

            {/* Row 4 — Affiliate breakdown + Cost detail */}
            <div className="mt-6 grid md:grid-cols-2 gap-6">
              <section className="border-2 border-[#111] p-6" data-testid="admin-affiliate-table">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bullet" />
                  <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                    03 — Affiliate clicks by partner
                  </h2>
                </div>
                {topPartners.length === 0 ? (
                  <div className="font-mono text-[11px] tracking-[0.2em] uppercase text-[#777] font-bold">
                    No clicks yet.
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-[#111]">
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Partner</th>
                        <th className="text-left py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Kind</th>
                        <th className="text-right py-2 font-mono text-[10px] tracking-[0.25em] uppercase text-[#555] font-bold">Clicks</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topPartners.map((p) => (
                        <tr key={`${p.partner}-${p.kind}`} className="border-b border-[#eee]">
                          <td className="py-2 font-display font-bold">{p.partner}</td>
                          <td className="py-2 text-[#777] uppercase font-mono text-[10px] tracking-[0.2em]">{p.kind}</td>
                          <td className="py-2 text-right font-display font-extrabold">{p.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>

              <section className="border-2 border-[#111] p-6" data-testid="admin-cost-detail">
                <div className="flex items-center gap-3 mb-4">
                  <span className="bullet" />
                  <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                    04 — Cost breakdown (estimate)
                  </h2>
                </div>
                <dl className="divide-y divide-[#eee]">
                  {[
                    ["Odds API credits used (est.)", stats.costs.odds_api_credits_used_estimate],
                    ["Odds API $ (est.)", `$${stats.costs.odds_api_dollar_estimate.toFixed(2)}`],
                    ["Cached odds calls", stats.costs.odds_cached_calls],
                    ["Cached props calls", stats.costs.props_cached_calls],
                    ["LLM calls (est.)", stats.costs.llm_calls_estimate],
                    ["LLM $ (est.)", `$${stats.costs.llm_dollar_estimate.toFixed(2)}`],
                    ["Game previews cached", stats.costs.previews_cached],
                    ["Betting insights cached", stats.costs.betting_cached],
                    ["Parlay pricings cached", stats.costs.parlays_cached],
                  ].map(([k, v]) => (
                    <div key={k} className="flex items-center justify-between py-2 text-sm">
                      <dt className="font-mono text-[11px] tracking-[0.15em] uppercase text-[#555] font-bold">{k}</dt>
                      <dd className="font-display font-extrabold">{typeof v === "number" ? v.toLocaleString() : v}</dd>
                    </div>
                  ))}
                </dl>
                <p className="mt-3 font-mono text-[10px] tracking-[0.2em] uppercase text-[#777] font-bold leading-relaxed">
                  {stats.costs.note}
                </p>
              </section>
            </div>

            {/* Row 5 — Credits / external dashboards */}
            <section className="mt-6 mb-14 border-2 border-[#111] p-6" data-testid="admin-credits-row">
              <div className="flex items-center gap-3 mb-4">
                <span className="bullet" />
                <h2 className="font-mono text-xs tracking-[0.3em] uppercase text-[#555]">
                  05 — Credits & external dashboards
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <a
                  href="https://emergent.sh/profile"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-[#111] p-5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors group"
                  data-testid="admin-link-emergent"
                >
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold">Emergent LLM Key</div>
                  <div className="mt-2 font-display text-xl font-extrabold leading-tight">View credit balance →</div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold opacity-70">Add balance · Auto top-up</div>
                </a>
                <a
                  href="https://the-odds-api.com/account/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-[#111] p-5 hover:bg-[#111] hover:text-[#f6f6f4] transition-colors"
                  data-testid="admin-link-odds-api"
                >
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold">The Odds API</div>
                  <div className="mt-2 font-display text-xl font-extrabold leading-tight">Open usage dashboard →</div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold opacity-70">500 credits/mo on free tier</div>
                </a>
                <div className="border-2 border-dashed border-[#999] p-5 opacity-60" data-testid="admin-link-stripe">
                  <div className="font-mono text-[10px] tracking-[0.3em] uppercase font-bold text-[#555]">Stripe · Coming soon</div>
                  <div className="mt-2 font-display text-xl font-extrabold leading-tight text-[#777]">MRR / ARR / Payouts</div>
                  <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase font-bold text-[#999]">Activates with Phase 3 billing</div>
                </div>
              </div>
            </section>
          </>
        )}
      </section>
    </main>
  );
}
